// Opening balances, the legacy records archive and open exceptions.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Archive, PlayCircle } from "lucide-react";

import { api, formatApiError } from "../../lib/api";
import { CAPABILITIES, can, fmtMinor, idemHeaders } from "../../lib/books";
import { Button, Empty, Field, Loading, Modal, Money, Pill, Section, Table, inputClass } from "./shared";

const CATEGORY_LABELS = {
  opening_balance_input: "Use in opening balances",
  opening_balance_payable_candidate: "Unpaid at go-live",
  historical_journal_candidate: "Can be imported after review",
  manual_reconciliation: "Needs a decision",
  legacy_reference: "Reference only",
  imported: "Already imported",
  deleted: "Deleted record",
};

export default function LegacyTab({ status, accounts, onChanged }) {
  const [opening, setOpening] = useState(null);
  const [legacy, setLegacy] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOpening, setShowOpening] = useState(false);
  const [integrity, setIntegrity] = useState(null);
  const canConfigure = can(status, CAPABILITIES.configure);

  const load = async () => {
    setLoading(true);
    try {
      const [ob, lg, ex] = await Promise.all([
        api.get("/finance/opening-balances"),
        api.get("/finance/legacy/records"),
        api.get("/finance/exceptions", { params: { status: "open" } }),
      ]);
      setOpening(ob.data);
      setLegacy(lg.data);
      setExceptions(ex.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runIntegrity = async () => {
    try {
      const { data } = await api.post("/finance/integrity/run");
      setIntegrity(data);
      toast[data.ok ? "success" : "error"](
        data.ok ? "Integrity checks passed." : `Integrity problems: ${data.critical_failures.join(", ")}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) return <Loading label="Reading your history..." />;

  const posted = (opening?.entries || []).filter((e) => e.status === "posted");

  return (
    <div className="space-y-4" data-testid="books-legacy">
      <Section title="Opening balances"
               description={`What the business already had on ${opening?.opening_balance_date}: cash, bank, ` +
                            "unpaid customer charges, unpaid bills and deposits you were holding."}
               actions={canConfigure && posted.length === 0 && (
                 <Button onClick={() => setShowOpening(true)} data-testid="books-opening-start">
                   Enter opening balances
                 </Button>)}
               testId="books-opening">
        {posted.length > 0 ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-sm">
            Opening balances posted as <span className="font-medium">{posted[0].number}</span> on{" "}
            {posted[0].date} ({fmtMinor(posted[0].total_minor)}). To change them, reverse that entry first.
          </div>
        ) : (
          <div className="space-y-3">
            <Table head={["Booking", "Held per the booking", "Legacy rows", "Status"]}
                   empty={((opening?.proposals?.deposits || []).length === 0 &&
                           (opening?.proposals?.advances || []).length === 0) &&
                          <Empty>No deposits or advances are waiting to be opened.</Empty>}>
              {[...(opening?.proposals?.deposits || []), ...(opening?.proposals?.advances || [])].map((row) => (
                <tr key={`${row.booking_id}-${row.held_minor}`}>
                  <td className="py-2 pr-3">{row.booking_reference}
                    <div className="text-xs text-slate-500">{row.customer_name}</div></td>
                  <td className="py-2 pr-3"><Money minor={row.held_minor} /></td>
                  <td className="py-2 pr-3">
                    {row.legacy_rows_total_minor === null ? "-" : <Money minor={row.legacy_rows_total_minor} />}
                  </td>
                  <td className="py-2 pr-3">
                    <Pill status={row.status === "match" ? "posted" : "warning"}>{row.status}</Pill>
                  </td>
                </tr>
              ))}
            </Table>
          </div>
        )}
      </Section>

      <Section title="Legacy finance records"
               description="Your old records stay exactly as they are. Nothing here is posted unless you review and import it."
               testId="books-legacy-records">
        <div className="flex flex-wrap gap-2 mb-3">
          {Object.entries(legacy?.counts || {}).map(([key, count]) => (
            <span key={key} className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600">
              {CATEGORY_LABELS[key] || key}: <span className="font-medium">{count}</span>
            </span>
          ))}
        </div>
        <Table head={["Record", "Date", { key: "a", label: "Amount", align: "right" }, "What to do"]}
               empty={(legacy?.items || []).length === 0 && <Empty>No legacy records.</Empty>}>
          {(legacy?.items || []).slice(0, 60).map((row) => (
            <tr key={row.id} data-testid={`books-legacy-${row.id}`}>
              <td className="py-2 pr-3">
                <div className="font-medium text-slate-900">{row.title}</div>
                <div className="text-xs text-slate-500">{row.record_type} · {row.status}</div>
              </td>
              <td className="py-2 pr-3 text-slate-600">{row.transaction_date}</td>
              <td className="py-2 pr-3 text-right"><Money minor={row.amount_minor} /></td>
              <td className="py-2 pr-3">
                <Pill status={row.books_category === "imported" ? "posted" : "info"}>
                  {CATEGORY_LABELS[row.books_category] || row.books_category}
                </Pill>
              </td>
            </tr>
          ))}
        </Table>
        <p className="mt-2 text-xs text-slate-500">
          <Archive className="w-3 h-3 inline mr-1" />{legacy?.note}
        </p>
      </Section>

      <Section title="Checks and exceptions"
               description="Things a person needs to look at. Nothing here is corrected automatically."
               actions={<Button variant="secondary" onClick={runIntegrity} data-testid="books-run-integrity">
                          <PlayCircle className="w-4 h-4" /> Run integrity check
                        </Button>}
               testId="books-exceptions">
        {integrity && (
          <div className={`mb-3 rounded-xl border p-3 text-sm ${
            integrity.ok ? "border-emerald-200 bg-emerald-50/60" : "border-rose-200 bg-rose-50/60"}`}
               data-testid="books-integrity-result">
            {integrity.ok
              ? "Every check passed: journals balance, hashes verify, documents and the ledger agree."
              : `Problems found: ${integrity.critical_failures.join(", ")}`}
          </div>
        )}
        <div className="space-y-2">
          {exceptions.length === 0 && <Empty>No open exceptions.</Empty>}
          {exceptions.map((row) => (
            <div key={row.id} className="flex items-start gap-2 rounded-xl border border-slate-200 p-3"
                 data-testid={`books-exception-${row.type}`}>
              <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${
                row.severity === "critical" ? "text-rose-600" : "text-amber-600"}`} />
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{row.title}</span>
                  <Pill status={row.severity}>{row.severity}</Pill>
                </div>
                <p className="text-sm text-slate-600">{row.message}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {showOpening && (
        <OpeningModal opening={opening} accounts={accounts} onClose={() => setShowOpening(false)}
                      onDone={() => { setShowOpening(false); load(); onChanged?.(); }} />
      )}
    </div>
  );
}

function OpeningModal({ opening, accounts, onClose, onDone }) {
  const payment = (accounts || []).filter((a) => a.is_payment_account && a.active);
  const [amounts, setAmounts] = useState({});
  const [deposits, setDeposits] = useState(
    Object.fromEntries([...(opening?.proposals?.deposits || [])].map((d) => [d.booking_id, true])));
  const [advances, setAdvances] = useState(
    Object.fromEntries([...(opening?.proposals?.advances || [])].map((d) => [d.booking_id, true])));
  const [documents, setDocuments] = useState([]);
  const [evidenceId, setEvidenceId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/administration/documents").then(({ data }) => setDocuments(data.items || [])).catch(() => {});
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/finance/opening-balances", {
        lines: Object.entries(amounts).filter(([, v]) => v && Number(v) > 0)
          .map(([account_id, debit]) => ({ account_id, debit, memo: "Opening balance" })),
        deposits: (opening?.proposals?.deposits || []).filter((d) => deposits[d.booking_id])
          .map((d) => ({ booking_id: d.booking_id, amount: (d.held_minor / 100).toFixed(2) })),
        advances: (opening?.proposals?.advances || []).filter((d) => advances[d.booking_id])
          .map((d) => ({ booking_id: d.booking_id, amount: (d.held_minor / 100).toFixed(2) })),
        receivables: [], payables: [],
        evidence_refs: evidenceId ? [{ type: "business_document", id: evidenceId }] : [],
        note: "Entered from the business's own records",
      }, { headers: idemHeaders() });
      toast.success("Opening balances posted.");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Opening balances" onClose={onClose} testId="books-opening-modal"
           description={`What you had on ${opening?.opening_balance_date}. Enter only what you can prove.`}>
      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Cash, bank and e-wallets</div>
        {payment.map((account) => (
          <div key={account.id} className="flex items-center gap-2">
            <span className="text-sm flex-1">{account.code} {account.name}</span>
            <input className={`${inputClass} w-36`} inputMode="decimal" placeholder="0.00"
                   data-testid={`books-opening-${account.code}`}
                   value={amounts[account.id] || ""}
                   onChange={(e) => setAmounts({ ...amounts, [account.id]: e.target.value })} />
          </div>
        ))}
      </div>

      {(opening?.proposals?.deposits || []).length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Deposits you are holding</div>
          {(opening?.proposals?.deposits || []).map((row) => (
            <label key={row.booking_id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!deposits[row.booking_id]}
                     data-testid={`books-opening-deposit-${row.booking_reference}`}
                     onChange={(e) => setDeposits({ ...deposits, [row.booking_id]: e.target.checked })} />
              {row.booking_reference} · <Money minor={row.held_minor} />
              {row.status !== "match" && <Pill status="warning">check</Pill>}
            </label>
          ))}
        </div>
      )}

      {(opening?.proposals?.advances || []).length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Advances you are holding</div>
          {(opening?.proposals?.advances || []).map((row) => (
            <label key={row.booking_id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!advances[row.booking_id]}
                     onChange={(e) => setAdvances({ ...advances, [row.booking_id]: e.target.checked })} />
              {row.booking_reference} · <Money minor={row.held_minor} />
            </label>
          ))}
        </div>
      )}

      <Field label="Evidence" hint="A business document that supports these figures (bank statement, cash count).">
        <select className={inputClass} value={evidenceId} data-testid="books-opening-evidence"
                onChange={(e) => setEvidenceId(e.target.value)}>
          <option value="">Select a document</option>
          {documents.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
        </select>
      </Field>
      <p className="text-xs text-slate-500">
        Anything that does not balance is recorded against Opening Balance Equity for your accountant to review.
      </p>
      <Button onClick={submit} busy={busy} data-testid="books-opening-save">Post opening balances</Button>
    </Modal>
  );
}
